import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { fetchProductsFromSearch } from '../../api/product';
import { getCategory } from '../../api/catalog';
import ProductCard from '../../components/ProductCard/ProductCard';
import { useAuth } from '../../context/AuthContext';
import FilterSidebar from './FilterSidebar';
import { categoryCover } from '../../utils/categoryCovers';
import { setSeoMeta, toAbsoluteUrl, upsertJsonLd, removeJsonLd } from '../../utils/seo';
import './Catalog.css';

function goodsLabel(n) {
  const count = Number(n) || 0;
  const abs = Math.abs(count) % 100;
  const d = abs % 10;
  let word = 'товаров';
  if (abs < 11 || abs > 14) {
    if (d === 1) word = 'товар';
    else if (d >= 2 && d <= 4) word = 'товара';
  }
  return `${count} ${word}`;
}

function pageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => set.add(n));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((n) => set.add(n));
  return [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
}

export default function CategoryPage() {
  const { category: topSlug, sub: subSlug } = useParams();
  const { user } = useAuth();
  const role = user?.role || 'guest';
  const [sp, setSp] = useSearchParams();

  const [cat, setCat] = useState(null);
  const [catLoading, setCatLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setCatLoading(true);
    (async () => {
      try {
        const data = await getCategory(topSlug);
        if (!mounted) return;
        setCat(data || null);
      } finally {
        if (mounted) setCatLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [topSlug]);

  const subcats = useMemo(() => cat?.subcategories || [], [cat]);
  const currentSub = useMemo(() => {
    if (!subSlug) return null;
    return subcats.find((s) => s.slug === subSlug) || null;
  }, [subSlug, subcats]);

  const apiParams = useMemo(() => {
    if (!subSlug || !currentSub?._id) return null;
    const obj = {
      topCategorySlug: topSlug,
      subcategoryId: String(currentSub._id),
      page: sp.get('page') || 1,
      limit: sp.get('limit') || 24,
      sort: sp.get('sort') || 'new',
    };

    ['minPrice', 'maxPrice', 'inStock', 'discounted'].forEach((k) => {
      const v = sp.get(k);
      if (v !== null && v !== undefined && v !== '') obj[k] = v;
    });

    const brands = sp.getAll('brand');
    if (brands.length) obj.brand = brands;

    const q = sp.get('q');
    if (q) obj.q = q;

    return obj;
  }, [topSlug, currentSub, subSlug, sp]);

  useEffect(() => {
    if (!apiParams) {
      setItems([]);
      setTotal(0);
      return;
    }
    let mounted = true;
    setProductsLoading(true);
    (async () => {
      try {
        const data = await fetchProductsFromSearch(apiParams);
        if (!mounted) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } finally {
        if (mounted) setProductsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [apiParams]);

  const title = cat?.name?.ru || 'Категория';
  const subTitle = currentSub?.name?.ru || currentSub?.slug;
  const page = Math.max(1, Number(sp.get('page') || 1) || 1);
  const limit = Math.max(1, Number(sp.get('limit') || 24) || 24);
  const pages = Math.max(1, Math.ceil(total / limit));
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = Math.min(page * limit, total);

  const goToPage = (nextPage) => {
    const n = Math.min(pages, Math.max(1, nextPage));
    const next = new URLSearchParams(sp);
    if (n <= 1) next.delete('page');
    else next.set('page', String(n));
    setSp(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const path = subSlug ? `/catalog/${topSlug}/${subSlug}` : `/catalog/${topSlug}`;
    const canonical = toAbsoluteUrl(path);
    const pageTitle = subTitle
      ? `${subTitle} — ${title} | LavSan`
      : `${title} — каталог LavSan`;
    const description = subTitle
      ? `${subTitle} в разделе ${title}. Фильтры по цене, бренду и наличию в интернет-магазине LavSan.`
      : `${title} в каталоге LavSan. Список подкатегорий.`;

    setSeoMeta({
      title: pageTitle,
      description,
      canonical,
      og: { type: 'website', url: canonical, title: pageTitle, description },
    });

    const breadcrumb = [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: toAbsoluteUrl('/') },
      { '@type': 'ListItem', position: 2, name: 'Каталог', item: toAbsoluteUrl('/catalog') },
      { '@type': 'ListItem', position: 3, name: title, item: toAbsoluteUrl(`/catalog/${topSlug}`) },
    ];
    if (subTitle) {
      breadcrumb.push({ '@type': 'ListItem', position: 4, name: subTitle, item: canonical });
    }

    upsertJsonLd('breadcrumb-catalog-category', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumb,
    });

    return () => removeJsonLd('breadcrumb-catalog-category');
  }, [topSlug, subSlug, title, subTitle]);

  // Категория → список подкатегорий (без фото)
  if (!subSlug) {
    return (
      <div className="catalog-page container">
        <nav className="catalog-crumbs">
          <Link to="/catalog">Каталог</Link>
          <span>/</span>
          <span>{catLoading ? '…' : title}</span>
        </nav>

        <header className="catalog-page__head catalog-page__head--with-cover">
          {!catLoading && (
            <div className="catalog-cat-banner">
              <img
                src={categoryCover(topSlug)}
                alt=""
                onError={(e) => {
                  e.currentTarget.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}
          <div>
            <h1>{catLoading ? 'Категория' : title}</h1>
            <p>Выберите подкатегорию</p>
          </div>
        </header>

        {catLoading ? (
          <div className="catalog-subs">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="catalog-sub is-skeleton" />
            ))}
          </div>
        ) : (
          <div className="catalog-subs">
            {subcats.map((s) => {
              const name = s.name?.ru || s.slug;
              return (
                <Link
                  key={s.slug}
                  to={`/catalog/${topSlug}/${s.slug}`}
                  className="catalog-sub"
                >
                  <span className="catalog-sub__name">{name}</span>
                  <span className="catalog-sub__meta">
                    <span className="catalog-sub__count">{goodsLabel(s.productCount)}</span>
                    <span className="catalog-sub__go" aria-hidden>
                      <FiArrowRight />
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {!catLoading && !subcats.length && (
          <p className="catalog-empty">В этой категории пока нет подкатегорий.</p>
        )}
      </div>
    );
  }

  // Подкатегория → товары
  return (
    <div className="catalog-page container">
      <nav className="catalog-crumbs">
        <Link to="/catalog">Каталог</Link>
        <span>/</span>
        <Link to={`/catalog/${topSlug}`}>{title}</Link>
        <span>/</span>
        <span>{subTitle || '…'}</span>
      </nav>

      <header className="catalog-page__head">
        <h1>{subTitle || title}</h1>
      </header>

      <div className="cat-layout">
        <FilterSidebar category={topSlug} />
        <div className="cat-content">
          <div className="catalog-toolbar">
            <div className="catalog-toolbar__count">
              {productsLoading
                ? 'Загрузка…'
                : total
                  ? `Найдено: ${total} · ${from}–${to}`
                  : 'Найдено: 0'}
            </div>
          </div>

          <div className="catalog__grid">
            {items.map((p) => (
              <ProductCard key={p._id} product={p} role={role} />
            ))}
          </div>

          {!productsLoading && !items.length && (
            <p className="catalog-empty">Ничего не найдено.</p>
          )}

          {!productsLoading && pages > 1 && (
            <nav className="catalog-pager" aria-label="Страницы">
              <button
                type="button"
                className="catalog-pager__btn"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                aria-label="Предыдущая страница"
              >
                <FiChevronLeft />
              </button>
              {pageList(page, pages).map((n, i, arr) => (
                <span key={n} className="catalog-pager__chunk">
                  {i > 0 && n - arr[i - 1] > 1 && (
                    <span className="catalog-pager__ellipsis">…</span>
                  )}
                  <button
                    type="button"
                    className={`catalog-pager__btn ${n === page ? 'is-active' : ''}`}
                    onClick={() => goToPage(n)}
                    aria-current={n === page ? 'page' : undefined}
                  >
                    {n}
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="catalog-pager__btn"
                disabled={page >= pages}
                onClick={() => goToPage(page + 1)}
                aria-label="Следующая страница"
              >
                <FiChevronRight />
              </button>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}
